import {
  AppointmentStatus,
  AppointmentStatusNumeric,
  ClassStatus,
  ClassStatusNumeric,
} from '@fitness-force/enum';

/**
 * Utility class for handling status enum conversions in audit trail events
 */
export class StatusEnumUtil {
  /**
   * Check if event is an appointment status field
   */
  static isAppointmentStatusField(event: any): boolean {
    return (
      event.entity_type === 'appointments' && event.fields === 'appt_status'
    );
  }

  /**
   * Check if event is a class status field
   */
  static isClassStatusField(event: any): boolean {
    return event.entity_type === 'class' && event.fields === 'class_status';
  }

  /**
   * Check if we should show enum status values
   */
  static shouldShowEnumStatus(event: any): boolean {
    return (
      this.isAppointmentStatusField(event) || this.isClassStatusField(event)
    );
  }

  /**
   * Convert numeric appointment status to enum label
   */
  static getAppointmentStatusLabel(statusValue: any): string {
    if (!statusValue) return 'N/A';

    const numericValue = Number(statusValue);
    if (isNaN(numericValue)) return statusValue.toString();

    // Convert numeric value to enum label
    switch (numericValue) {
      case AppointmentStatusNumeric.Booked:
        return AppointmentStatus.Booked;
      case AppointmentStatusNumeric.Attended:
        return AppointmentStatus.Attended;
      case AppointmentStatusNumeric.NoShow:
        return AppointmentStatus.NoShow;
      case AppointmentStatusNumeric.Cancelled:
        return AppointmentStatus.Cancelled;
      default:
        return statusValue.toString();
    }
  }

  /**
   * Convert numeric class status to enum label
   */
  static getClassStatusLabel(statusValue: any): string {
    if (!statusValue) return 'N/A';

    const numericValue = Number(statusValue);
    if (isNaN(numericValue)) return statusValue.toString();

    // Convert numeric value to enum label
    switch (numericValue) {
      case ClassStatusNumeric.Scheduled:
        return ClassStatus.Scheduled;
      case ClassStatusNumeric.Cancelled:
        return ClassStatus.Cancelled;
      case ClassStatusNumeric.Completed:
        return ClassStatus.Completed;
      default:
        return statusValue.toString();
    }
  }

  /**
   * Get enum status label based on event type
   */
  static getEnumStatusLabel(event: any, value: any): string {
    if (this.isAppointmentStatusField(event)) {
      return this.getAppointmentStatusLabel(value);
    } else if (this.isClassStatusField(event)) {
      return this.getClassStatusLabel(value);
    }
    return value?.toString() || 'N/A';
  }

  /**
   * Get status severity for styling (returns CSS class instead of severity)
   */
  static getStatusStyleClass(event: any, value: any): string {
    const label = this.getEnumStatusLabel(event, value);

    // Appointment status colors
    if (this.isAppointmentStatusField(event)) {
      switch (label) {
        case AppointmentStatus.Booked:
          return 'bg-blue-100 text-blue-800 border-blue-200';
        case AppointmentStatus.Attended:
          return 'bg-green-100 text-green-800 border-green-200';
        case AppointmentStatus.NoShow:
          return 'bg-orange-100 text-orange-800 border-orange-200';
        case AppointmentStatus.Cancelled:
          return 'bg-red-100 text-red-800 border-red-200';
        default:
          return 'bg-gray-100 text-gray-800 border-gray-200';
      }
    }

    // Class status colors
    if (this.isClassStatusField(event)) {
      switch (label) {
        case ClassStatus.Scheduled:
          return 'bg-blue-100 text-blue-800 border-blue-200';
        case ClassStatus.Completed:
          return 'bg-green-100 text-green-800 border-green-200';
        case ClassStatus.Cancelled:
          return 'bg-red-100 text-red-800 border-red-200';
        default:
          return 'bg-gray-100 text-gray-800 border-gray-200';
      }
    }

    return 'bg-gray-100 text-gray-800 border-gray-200';
  }

  /**
   * Get status icon based on event type and value
   */
  static getStatusIcon(event: any, value: any): string {
    const label = this.getEnumStatusLabel(event, value);

    // Appointment status icons
    if (this.isAppointmentStatusField(event)) {
      switch (label) {
        case AppointmentStatus.Booked:
          return 'pi pi-calendar';
        case AppointmentStatus.Attended:
          return 'pi pi-check-circle';
        case AppointmentStatus.NoShow:
          return 'pi pi-exclamation-triangle';
        case AppointmentStatus.Cancelled:
          return 'pi pi-times-circle';
        default:
          return 'pi pi-info-circle';
      }
    }

    // Class status icons
    if (this.isClassStatusField(event)) {
      switch (label) {
        case ClassStatus.Scheduled:
          return 'pi pi-clock';
        case ClassStatus.Completed:
          return 'pi pi-check-circle';
        case ClassStatus.Cancelled:
          return 'pi pi-times-circle';
        default:
          return 'pi pi-info-circle';
      }
    }

    return 'pi pi-info-circle';
  }
}
